import {FeedbackOrigin} from '../../components/ai/DisclosureControls';
import {getFeedbackPreference} from '../../components/ai/aiDisclosure';
import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {Button, Card} from '@betareader/design-system';
import Icon from '../../components/AppIcon';
import {useDispatch, useSelector} from '../../reduxHooks';
import styled from 'styled-components';
// i18n
import {useTranslation} from 'react-i18next';

import {
  removeInlineCommentPlaceholder,
  setActiveInlineComment,
  updateCommentsWithSidebarTop,
  submitComment,
  submitReply
} from '../../modules/comment';

import CommentElement from './commentElement';
import {authUtil, uxAnalyticsUtil} from '../../utils';

/**
 * Updates top attribute of each visible comment in the sidebar, for absolute css placement
 * @param {*} commentsWithOverlappingLayout
 * @returns
 */
const getCommentListLayout = (commentsWithOverlappingLayout: any[]) => {
  const updatedCommentList = [...commentsWithOverlappingLayout].reduce(
    (accumulatedResult, _currentComment) => {
      const _previousComment = accumulatedResult[accumulatedResult.length - 1];
      const _previousBottom =
        (_previousComment?.top ?? 0) + (_previousComment?.height ?? 0); // 0 if non-existent, otherwise top + height

      let _currentTop = Math.max(0, _currentComment?.top); // top should not be less than 0 (would be outside view)
      _currentTop = Math.max(_currentTop, _previousBottom + 5); // top should be at least 5 pixels below the previous one
      accumulatedResult.push({
        ..._currentComment,
        top: _currentTop
      });
      return accumulatedResult;
    },
    []
  );

  return updatedCommentList;
};

const InlineCommentWrapper = styled.div<{top: number}>`
  position: absolute;
  top: ${({top}) => top}px;
  width: var(--sidebar-width);
  display: flex;
  flex-direction: column;
`;

interface Props {
  positioning: 'sidebar' | 'bottom';
  disableLeavingComments?: boolean;
  showOnly?: {type?: string; status?: string[]};
  enableFilters?: boolean;
  compact?: boolean;
}

const InlineCommentsList: React.FC<Props> = ({
  compact,
  disableLeavingComments,
  enableFilters,
  positioning,
  showOnly
}) => {
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const currentBook = useSelector(state => state.book.currentBook);
  const currentSpineIndex = useSelector(
    state => state.readerApp.currentSpineIndex
  );
  const comments = useSelector(state => state.comment.comments);
  const filteredReaders = useSelector(state => state.comment.filteredReaders);
  const activeInlineComment = useSelector(
    state => state.comment.activeInlineComment
  );
  const currentLanguage = useSelector(state => state.user.currentLanguage);
  const fetchingComments = useSelector(state => state.comment.fetchingComments);

  const [comment, setComment] = useState<string>();
  const [sidebarCommentElementSizes, setSidebarCommentElementSizes] = useState<Record<string, number>>({});
  const [aiOrigin, setAiOrigin] = useState(getFeedbackPreference);
  const [replyForm, setReplyForm] = useState(undefined);

  const [height, setHeight] = useState(0);
  const sidebarListRef = useRef<HTMLDivElement | null>(null);

  // get currently filtered comments
  const filteredComments = useMemo(() => {
    let inlineComments = [];

    if (activeInlineComment) {
      return comments?.filter((c: any) => c._id === activeInlineComment);
    }
    // otherwise, go through all
    inlineComments = comments?.filter((comment: any) => {
      if (
        showOnly &&
        showOnly.status &&
        comment.status &&
        !showOnly.status.includes(comment.status)
      ) {
        return false;
      }
      if (
        filteredReaders !== undefined &&
        comment.user._id !== filteredReaders._id
      ) {
        return false;
      }
      if (comment.kind !== 'InlineComment') {
        return false;
      }
      return true;
    });
    return inlineComments;
  }, [comments, filteredReaders, activeInlineComment, showOnly]);

  useEffect(() => {
    setSidebarCommentElementSizes(prev => {
      const updatedSizes: Record<string, number> = {};
      Object.entries(prev).forEach(([commentId, hgt]) => {
        const existingEntry = filteredComments?.find((c: any) => c._id === commentId);
        if (existingEntry) {
          updatedSizes[commentId] = height;
        }
      });
      return updatedSizes;
    }); // reset the sizes when the filter changes
  }, [filteredComments?.length]);

  useEffect(() => {
    /**
     * Returns the total display height of all visible comments in the sidebar
     */
    const totalHeight =
      comments?.reduce(
        (accumulator: number, _comment: any) =>
          accumulator +
          (_comment.layout?.sidebar?.getBoundingClientRect()?.height ?? 0),
        0
      ) ?? 0;
    setHeight(totalHeight);
  }, [comments]);

  const commentMeasures = useMemo(
    () => Object.keys(sidebarCommentElementSizes).length,
    [sidebarCommentElementSizes]
  );

  useEffect(() => {
    const escapeListener = (event: any) => {
      if (event.keyCode === 27) {
        deactivate();
      }
    };

    document.addEventListener('keydown', escapeListener, false);

    return () => {
      document.removeEventListener('keydown', escapeListener, false);
    };
  }, []);

  /**
   * Repositions comment elements each time total height changes
   */
  useEffect(() => {
    if (commentMeasures === filteredComments?.length) {
      // only update positions when all visible measures are in
      updateCommentPositions();
    }
  }, [activeInlineComment, commentMeasures, height, filteredComments?.length]);

  /**
   * When entering focused mode (single comment), run position update immediately
   * so the card aligns with the inline highlight without waiting for height measure.
   */
  useEffect(() => {
    if (activeInlineComment && filteredComments?.length === 1) {
      const id = requestAnimationFrame(() => {
        updateCommentPositions();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [activeInlineComment, filteredComments?.length]);

  const getActiveInlineComment = useCallback(() => {
    if (!comments || !activeInlineComment) return null;
    return comments.find((_comment: any) => _comment._id === activeInlineComment);
  }, [comments, activeInlineComment]);

  const handleCommentUpdate = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  const handleShowAllClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    deactivate();
  };

  const handleSubmitComment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (aiOrigin !== 'personal') return;
    const idToken = await authUtil.getFreshIdToken();

    dispatch(
      submitComment(
        idToken,
        currentBook._id,
        currentBook.parts[currentSpineIndex]._id,
        comment,
        {aiOrigin},
        (savedId: any) => { if (savedId) { setReplyForm(undefined); setComment(''); setAiOrigin('undeclared'); } }
      )
    );

    uxAnalyticsUtil.trackEvent({
      category: 'Reading',
      action: 'Left comment'
    });

  };

  const _submitReply = async (_comment: any, reply: any) => {
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      submitReply(idToken, _comment, reply, (callback: any) => {
        if (callback) {
          updateCommentPositions();
        }
      })
    );
  };

  // adjust the position of comments in the right hand comment list
  const updateCommentPositions = () => {
    // filter out inline comments
    const notFilteredComments: any = [];
    const inlineCommentBlocks = document.getElementsByClassName(
      'inline-comment-block'
    );
    const filteredComments = comments?.filter((_comment: any) => {
      if (activeInlineComment && _comment._id !== activeInlineComment) {
        notFilteredComments.push(_comment);
        return false; // don't include if we have an active comment and this is not the one
      }
      // only show comments with matching statuses
      if (
        showOnly &&
        showOnly.status &&
        _comment.status &&
        !showOnly.status.includes(_comment.status)
      ) {
        notFilteredComments.push(_comment);
        return false;
      }
      // only show comments with matching readers/users
      if (
        filteredReaders !== undefined &&
        _comment.user._id !== filteredReaders._id
      ) {
        notFilteredComments.push(_comment);
        return false;
      }
      // only show inline comments (chapter comments are shown below)
      if (_comment.kind !== 'InlineComment') {
        notFilteredComments.push(_comment);
        return false;
      }
      return true;
    });

    // get the boundary of the editor and update position
    const updatedCommentLayouts: any[] = []; // placeholder for comments with updated position
    const listContainer = sidebarListRef?.current ?? null;
    const textEditorReference =
      document.getElementsByClassName('DraftEditor-root')[0];
    const textEditorTop = textEditorReference
      ? textEditorReference.getBoundingClientRect().top
      : 0;
    const listContainerTop = listContainer
      ? listContainer.getBoundingClientRect().top
      : null;

    let allComments = [];
    if (filteredComments?.length > 0) {
      // go through all visible comments
      filteredComments.forEach((_comment: any) => {
        // Find the current height of the comment element in the sidebar (fallback 0 when card just mounted)
        const sidebarPlacementHeight = Math.round(
          sidebarCommentElementSizes[_comment?._id] ?? 0
        );
        // Find the current top and left position of the comment element in the inline text
        const inlineBlockElement = inlineCommentBlocks.namedItem(
          `br-inline-cid-${_comment._id}`
        );
        const inlineRect = inlineBlockElement?.getBoundingClientRect();
        const inlinePlacementTop = Math.round(inlineRect?.top ?? 0);
        const inlinePlacementLeft = Math.round(inlineRect?.left ?? 0);

        let top: number;
        if (listContainerTop !== null) {
          top = Math.round(inlinePlacementTop - listContainerTop);
        } else {
          let commentListTop = 51;
          if (filteredReaders !== undefined) {
            commentListTop = 25;
          }
          top = inlinePlacementTop + commentListTop - textEditorTop;
        }

        const left = inlinePlacementLeft;
        const height = sidebarPlacementHeight;
        const bottom = top + height;
        const newCommentLayout = {
          ..._comment,
          top,
          left,
          height,
          bottom
        };
        updatedCommentLayouts.push(newCommentLayout);
      });
      // sort list
      const commentsSortedByInLineAppearance = sortInlineComments(
        updatedCommentLayouts
      );
      // In focused mode (single comment), skip stacking so card aligns with inline highlight
      const commentListLayout =
        updatedCommentLayouts.length === 1
          ? commentsSortedByInLineAppearance
          : getCommentListLayout(commentsSortedByInLineAppearance);
      // add back chaptercomments and remaining inlinecomments, since we want to keep them in state as well to show
      allComments = commentListLayout.concat(notFilteredComments);
    }
    // update state with the updated inline comment elements
    dispatch(updateCommentsWithSidebarTop(allComments));
  };

  const getBottomBarInlineCommentsList = () => {
    const currentComment = getActiveInlineComment();
    if (!currentComment) return null;
    const hasReplies = currentComment.replies?.length > 0;
    return (
      <Card className='br-text inlineCommentsList' style={{padding: 16}}>
        <div style={{display: 'flex', justifyContent: 'flex-end'}}>
          <Icon name='close' onClick={deactivate} aria-label={t('Close', 'Close')} />
        </div>
        <div style={{display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr) 40px', alignItems: 'center', gap: 8}}>
          <Icon name='chevron left' onClick={previousComment} aria-label={t('PreviousComment', 'Previous comment')} />
          <CommentElement
            onSizeChange={handleCommentSizeChange}
            comment={currentComment}
            isActive
            hasReplies={hasReplies}
            inReaderView
            onFocusInContent={activateComment}
          />
          <Icon name='chevron right' onClick={nextComment} aria-label={t('NextComment', 'Next comment')} />
        </div>
      </Card>
    );
  };

  const handleCommentSizeChange = (
    commentId: string,
    height: number | undefined
  ) => {
    if (height !== undefined) {
      setSidebarCommentElementSizes(prev => ({
        ...prev,
        [commentId]: height
      }));
    }
  };

  const sortInlineComments = (unSortedInlineComments: any) => {
    const sorted = unSortedInlineComments.sort((commentA: any, commentB: any) => {
      if (commentA.top === commentB.top) {
        return commentA.left - commentB.left;
      }
      return commentA.top - commentB.top;
    });
    return sorted;
  };

  const deactivate = () => {
    dispatch(setActiveInlineComment({ commentId: undefined, show: undefined }));
    dispatch(removeInlineCommentPlaceholder());
  };

  const nextComment = () => {
    const currentIndex = comments?.findIndex(
      (comment: any) => comment._id === activeInlineComment
    );
    if (currentIndex >= 0) {
      const nextIndex = (currentIndex + 1) % comments.length;
      const nextActiveInlineComment = comments[nextIndex];
      activateComment(nextActiveInlineComment);
    }
  };

  const previousComment = () => {
    const currentIndex = comments?.findIndex(
      (comment: any) => comment._id === activeInlineComment
    );
    if (currentIndex >= 0) {
      let nextIndex = (currentIndex - 1) % comments.length;
      if (nextIndex === -1) {
        nextIndex = comments.length - 1;
      }
      const nextActiveInlineComment = comments[nextIndex];
      activateComment(nextActiveInlineComment);
    }
  };

  const activateComment = (inlineComment: any) => {
    if (inlineComment && inlineComment.layout && inlineComment.layout.inline) {
      dispatch(
        setActiveInlineComment({commentId: inlineComment._id, show: 'inline'})
      );

      inlineComment.layout.inline.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start'
      });
    }
  };

  const sidebarInlineCommentsList = useMemo(() => {
    if (filteredComments?.length > 0) {
      const commentList = filteredComments.map((_comment: any, index: any) => {
        const hasReplies = _comment?.replies?.length > 0;
        const isActive = activeInlineComment === _comment._id;
        let hide = false;
        if (!!activeInlineComment && !isActive) {
          // if there is an active comment, don't show any other comments
          return null;
          hide = true;
        }
        return (
          <InlineCommentWrapper top={_comment.top || 0} key={_comment._id}>
            {!!activeInlineComment &&
              isActive &&
              activeInlineComment !== 'placeHolder' && (
                // if there is an active comment and this is the one,
                // show expand buttons on either side
                <Button variant='ghost' size='small' onClick={handleShowAllClick}>
                  {t('ShowAllComments')}
                </Button>
              )}
            <CommentElement
              isActive={isActive}
              onSizeChange={handleCommentSizeChange}
              comment={_comment}
              hasReplies={hasReplies}
              enableFilters={!!enableFilters}
              inReaderView
              onFocusInContent={activateComment}
            />
          </InlineCommentWrapper>
        );
      });
      return commentList;
    } else {
      return null;
    }
  }, [activeInlineComment, filteredComments]);

  if (fetchingComments) return null;
  // filter out inline comments according to current filters
  const lastInlineComment =
    filteredComments && filteredComments[filteredComments.length - 1];
  const inlineCommentsBottomY = lastInlineComment
    ? lastInlineComment.bottom
    : document.documentElement.clientHeight;
  if (positioning === 'sidebar') {
    return (
      <div
        ref={sidebarListRef}
        style={{
          position: 'relative',
          margin: 0,
          padding: 0,
          height: inlineCommentsBottomY
        }}>
        <div style={{margin: 0, padding: 0}} onClick={deactivate}>
        {!disableLeavingComments && (
          <form onSubmit={handleSubmitComment}>
            <FeedbackOrigin value={aiOrigin} onChange={setAiOrigin} />
            <label htmlFor='inline-comment-text'>{t('LeaveComment')}</label>
            <textarea id='inline-comment-text' onChange={handleCommentUpdate}
              value={comment ?? ''} className='br-field__input' />
            <Button
              type='submit'
              disabled={aiOrigin !== 'personal' || !comment || comment?.length === 0}
            >{t('LeaveComment')}</Button>
          </form>
        )}
        {filteredComments?.length === 0 && (
          <p>{t('NoCommentsToShow')}</p>
        )}
        {sidebarInlineCommentsList}
        </div>
      </div>
    );
  } else if (
    positioning === 'bottom' &&
    !!activeInlineComment &&
    getActiveInlineComment()?.kind === 'InlineComment'
  ) {
    return getBottomBarInlineCommentsList();
  }
  return null;
};

export default InlineCommentsList;
